import type { ErrorBodyData } from '@@/types/OrganizationsAPI';

export function apiErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;

  const err = error as Record<string, unknown>;

  if (typeof err.statusCode === 'number') return err.statusCode;
  if (typeof err.status === 'number') return err.status;

  const response = err.response;
  if (response && typeof response === 'object' && typeof (response as Record<string, unknown>).status === 'number') {
    return (response as Record<string, unknown>).status as number;
  }

  return undefined;
}

/*
 * 403 only: the operator has no live organization_service grant, or no mirrored principal in
 * organization-service. It's "not granted", not a bug. A 401 is deliberately excluded —
 * useAuthFetch answers it with a silent refresh and retry, so one that still surfaces is a
 * genuine failure, not an access decision.
 */
export function isAccessDeniedError(error: unknown): boolean {
  return apiErrorStatus(error) === 403;
}

/** An unknown, sub-org or merged client UUID. */
export function isNotFoundError(error: unknown): boolean {
  return apiErrorStatus(error) === 404;
}

/**
 * Why an admin write failed, as the UI acts on it. The service's 422 bodies carry only a message,
 * never a machine-readable code, so a refusal is shown, never branched on.
 */
export type OrgWriteFailure =
  | { kind: 'forbidden' }
  | { kind: 'notFound' }
  | { kind: 'refused'; message: string }
  | { kind: 'retryable'; message?: string };

const REFUSED_FALLBACK = 'The organization service refused this change.';

export function errorBody(error: unknown): Partial<ErrorBodyData> {
  const data = error && typeof error === 'object' ? (error as Record<string, unknown>).data : undefined;
  return data && typeof data === 'object' ? (data as Partial<ErrorBodyData>) : {};
}

/*
 * A 401 never arrives here: useAuthFetch renews the session and retries once, or redirects to
 * sign-in. A 409 that reaches this carries no conflicts list (a merge's conflicts resolve as its
 * result): it is the service's duplicate-insert race, not a review state, so it is offered as a
 * retry.
 */
export function classifyOrgWriteError(error: unknown): OrgWriteFailure {
  const status = apiErrorStatus(error);
  const body = errorBody(error);

  switch (status) {
    case 403:
      return { kind: 'forbidden' };
    case 404:
      return { kind: 'notFound' };
    case 400:
    case 422:
      return { kind: 'refused', message: body.message || REFUSED_FALLBACK };
    case 409:
      return body.message ? { kind: 'retryable', message: body.message } : { kind: 'retryable' };
    default:
      return { kind: 'retryable' };
  }
}
